/**
 * CashReconciliationPage — `/mail/cash-recon`.
 *
 * Live operator surface backed by service-mail
 *   POST /v1/cash-reconciliations
 *   GET  /v1/cash-reconciliations
 *   POST /v1/cash-reconciliations/:id/finalize
 *
 * Two stacked panels:
 *   - Operator self panel: submit form (opening balance, cash in, cash out,
 *     actual closing, optional notes) + my recent history.
 *   - Manager panel: the queue, defaulting to DISCREPANCY rows, with a
 *     "Finalize" button per row.
 */
import { type FormEvent, type JSX, useEffect, useMemo, useState } from 'react';
import { Banknote, Calculator, CheckCircle2, Loader2 } from 'lucide-react';

import { decodeStaffClaims } from '../../lib/auth/jwt';
import { getToken } from '../../lib/auth/tokenStore';
import {
  finalizeCashReconciliation,
  listCashReconciliations,
  submitCashReconciliation,
  type CashReconciliationDto,
  type CashReconciliationStatus,
} from '../../lib/api/cashReconciliation';
import { PageHeader } from '../shared/StaffUI';

function formatNgn(minor: number): string {
  const sign = minor < 0 ? '-' : '';
  const absMajor = Math.abs(minor) / 100;
  return `${sign}₦${absMajor.toLocaleString()}`;
}

function statusPill(status: CashReconciliationStatus): string {
  switch (status) {
    case 'RECONCILED': return 'bg-green-50 text-green-700';
    case 'DISCREPANCY': return 'bg-red-50 text-red-700';
    case 'SUBMITTED': return 'bg-yellow-50 text-yellow-700';
    case 'DRAFT': return 'bg-gray-100 text-gray-600';
  }
}

export function CashReconciliationPage(): JSX.Element {
  const [outletCode, setOutletCode] = useState('NG-LAG-VI-001');
  const [opening, setOpening] = useState('');
  const [cashIn, setCashIn] = useState('');
  const [cashOut, setCashOut] = useState('');
  const [actual, setActual] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<CashReconciliationDto | null>(null);

  const [myHistory, setMyHistory] = useState<readonly CashReconciliationDto[]>([]);
  const [queue, setQueue] = useState<readonly CashReconciliationDto[]>([]);
  const [statusFilter, setStatusFilter] = useState<CashReconciliationStatus | ''>('DISCREPANCY');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const claims = decodeStaffClaims(getToken());
  const myOperatorId = claims?.subject ?? '';

  const toMinor = (s: string): number => {
    const f = parseFloat(s);
    return Number.isNaN(f) ? 0 : Math.round(f * 100);
  };

  const expected = useMemo(() => toMinor(opening) + toMinor(cashIn) - toMinor(cashOut), [opening, cashIn, cashOut]);
  const variance = useMemo(() => toMinor(actual) - expected, [actual, expected]);
  const willDiscrepancy = Math.abs(variance) > 10_000;

  async function refreshLists(): Promise<void> {
    try {
      const [mine, q] = await Promise.all([
        myOperatorId ? listCashReconciliations({ operatorId: myOperatorId, pageSize: 25 }) : Promise.resolve({ items: [], total: 0, page: 1, pageSize: 25 }),
        listCashReconciliations({
          ...(statusFilter && { status: statusFilter }),
          pageSize: 25,
        }),
      ]);
      setMyHistory(mine.items);
      setQueue(q.items);
      setListError(null);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load reconciliations');
    }
  }

  useEffect(() => {
    void refreshLists();
    // note: deps intentionally narrow — rule not enabled in this config
  }, [statusFilter, myOperatorId]);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    setSubmitResult(null);
    try {
      const created = await submitCashReconciliation({
        outletCode,
        openingBalanceMinor: toMinor(opening),
        cashInMinor: toMinor(cashIn),
        cashOutMinor: toMinor(cashOut),
        actualClosingMinor: toMinor(actual),
        ...(notes && { notes }),
      });
      setSubmitResult(created);
      // Reset on success
      setOpening('');
      setCashIn('');
      setCashOut('');
      setActual('');
      setNotes('');
      await refreshLists();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function finalize(id: string): Promise<void> {
    setBusyId(id);
    try {
      await finalizeCashReconciliation(id);
      await refreshLists();
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Finalize failed');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-5" data-testid="cash-reconciliation-page">
      <PageHeader
        title="Cash Reconciliation"
        description="End-of-shift drawer reconciliation. |variance| ≤ ₦100 auto-RECONCILED; otherwise DISCREPANCY."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <form
          onSubmit={(e) => void onSubmit(e)}
          data-testid="cashrecon-self-form"
          className="bg-white border border-border rounded-xl p-5 space-y-3"
        >
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Banknote className="w-4 h-4 text-nipost-green" /> Submit reconciliation
          </h3>
          <div>
            <label htmlFor="outlet" className="text-xs font-semibold text-gray-700">Outlet code</label>
            <input id="outlet" type="text" value={outletCode} onChange={(e) => setOutletCode(e.target.value)} className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-lg" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="open" className="text-xs font-semibold text-gray-700">Opening (₦)</label>
              <input id="open" type="number" step="0.01" min="0" value={opening} onChange={(e) => setOpening(e.target.value)} data-testid="cashrecon-opening" className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-lg" />
            </div>
            <div>
              <label htmlFor="cin" className="text-xs font-semibold text-gray-700">Cash in (₦)</label>
              <input id="cin" type="number" step="0.01" min="0" value={cashIn} onChange={(e) => setCashIn(e.target.value)} data-testid="cashrecon-cashin" className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-lg" />
            </div>
            <div>
              <label htmlFor="cout" className="text-xs font-semibold text-gray-700">Cash out (₦)</label>
              <input id="cout" type="number" step="0.01" min="0" value={cashOut} onChange={(e) => setCashOut(e.target.value)} data-testid="cashrecon-cashout" className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-lg" />
            </div>
            <div>
              <label htmlFor="act" className="text-xs font-semibold text-gray-700">Actual closing (₦)</label>
              <input id="act" type="number" step="0.01" min="0" value={actual} onChange={(e) => setActual(e.target.value)} data-testid="cashrecon-actual" className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-lg" />
            </div>
          </div>
          <div>
            <label htmlFor="notes" className="text-xs font-semibold text-gray-700">Notes</label>
            <input id="notes" type="text" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-lg" />
          </div>
          <div className="border-t border-border pt-3 grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2 text-gray-700">
              <Calculator className="w-4 h-4 text-muted" />
              <span>Expected:</span>
              <span className="font-semibold">{formatNgn(expected)}</span>
            </div>
            <div data-testid="cashrecon-variance">
              <span className="text-gray-700">Variance: </span>
              <span className={`font-bold ${variance === 0 ? 'text-gray-900' : willDiscrepancy ? 'text-red-700' : 'text-yellow-700'}`}>
                {formatNgn(variance)}
              </span>
              {willDiscrepancy ? <span className="text-xs text-red-700 ml-1">(will be DISCREPANCY)</span> : null}
            </div>
          </div>
          {submitError ? <p className="text-sm text-red-700">{submitError}</p> : null}
          {submitResult ? (
            <p className="text-sm" data-testid="cashrecon-submit-result">
              <span className="font-semibold">Submitted:</span>{' '}
              <span className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded ${statusPill(submitResult.status)}`}>{submitResult.status}</span>
            </p>
          ) : null}
          <button
            type="submit"
            disabled={submitting || !opening || !actual}
            data-testid="cashrecon-submit"
            className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit'}
          </button>
        </form>

        <div className="bg-white border border-border rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3">My recent reconciliations</h3>
          {myHistory.length === 0 ? (
            <p className="text-sm text-muted">None yet.</p>
          ) : (
            <ul className="space-y-2" data-testid="cashrecon-myhistory">
              {myHistory.slice(0, 6).map((it) => (
                <li key={it.id} className="border border-border rounded p-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-mono">{it.outletCode}</span>
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded ${statusPill(it.status)}`}>{it.status}</span>
                  </div>
                  <div className="text-muted mt-0.5">
                    var {formatNgn(it.varianceMinor)} · {new Date(it.createdAt).toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="bg-white border border-border rounded-xl p-5" data-testid="cashrecon-manager-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Manager queue</h3>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as CashReconciliationStatus | '')}
            data-testid="cashrecon-status-filter"
            className="px-2 py-1.5 text-sm border border-border rounded-lg"
          >
            <option value="">All</option>
            <option value="DISCREPANCY">Discrepancies</option>
            <option value="RECONCILED">Reconciled</option>
            <option value="SUBMITTED">Submitted</option>
          </select>
        </div>
        {listError ? <p className="text-sm text-red-700">{listError}</p> : null}
        {queue.length === 0 ? (
          <p className="text-sm text-muted" data-testid="cashrecon-queue-empty">No matching reconciliations.</p>
        ) : (
          <ul className="space-y-2" data-testid="cashrecon-queue-list">
            {queue.map((it) => (
              <li
                key={it.id}
                data-testid={`cashrecon-row-${it.id}`}
                className="border border-border rounded-lg p-3 flex items-start justify-between gap-3"
              >
                <div className="text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold">{it.operatorId}</span>
                    <span className="text-muted">{it.outletCode}</span>
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded ${statusPill(it.status)}`}>{it.status}</span>
                  </div>
                  <div className="text-muted mt-0.5">
                    Expected {formatNgn(it.expectedClosingMinor)} · Actual {formatNgn(it.actualClosingMinor)} · Variance {formatNgn(it.varianceMinor)}
                  </div>
                  <div className="text-muted mt-0.5">{new Date(it.createdAt).toLocaleString()}</div>
                  {it.reconciledByOperatorId ? (
                    <div className="text-muted mt-0.5">Finalized by {it.reconciledByOperatorId}</div>
                  ) : null}
                </div>
                {it.status === 'DISCREPANCY' && !it.reconciledByOperatorId ? (
                  <button
                    type="button"
                    onClick={() => void finalize(it.id)}
                    disabled={busyId === it.id}
                    data-testid={`cashrecon-finalize-${it.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-nipost-green text-white text-xs font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Finalize
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
